import * as cx from 'classnames';
import { COMMENT_REMOVE_COMPLAINTS } from 'mk/autogenerated/translations/Comment.9cd56ef7351e3707592678245e3f78b9'
import {
    boldize,
    getInlinedImages,
    linebreaksbr,
    mentionize,
    replaceImageMarks,
    urlize,
} from 'mk/bazaar/common/filters';
import { isAuthenticatedAs } from 'mk/bazaar/common/userUtils';
import CommentBody from 'mk2/apps/blogs/components/CommentBody';
import CommentFooter from 'mk2/apps/blogs/components/CommentFooter';
import CommentHeader from 'mk2/apps/blogs/components/CommentHeader';
import CommentMenu from 'mk2/apps/blogs/components/CommentMenu';
import { CommentEntity } from 'mk2/apps/blogs/schemas';
import { CounsellingEntity } from 'mk2/apps/forum/schemas';
import { linkize } from 'mk2/apps/forum/utils';
import LoadingPlaceholder from 'mk2/components/LoadingPlaceholder';
import Panel from 'mk2/components/Panel';
import Complaints from 'mk2/containers/Complaints/Complaints';
import { UserEntity } from 'mk2/schemas';
import * as React from 'react';
import styles from './Comment.mscss';

interface OwnState {
    hasHover: boolean;
    collapsed: boolean;
}

interface OwnProps {
    className?: string;
    comment: CommentEntity;
    highlighted: boolean;
    requestUser: UserEntity;
    requestUserPermissions: string[];
    isMobile: boolean;
    inForum: boolean;
    inGroups?: boolean;
    followLinks?: boolean;
    moderators?: UserEntity[];
    counselling?: CounsellingEntity;
    location?: string;

    hasBodyHiddenText: boolean;
    hasBodyDeletedStatus: boolean;
    hasFooterLike: boolean;
    hasFooterReply: boolean;
    hasMenu: boolean;
    hasMenuReport: boolean;
    hasMenuEdit: boolean;
    hasMenuDelete: boolean;
    hasMenuMoveUp: boolean;

    onReply(username: string);
    onDelete(commentId: number);
    onReport(entityType: string, commentId: number);
    onToggleMenuMore?(commentId: number);
    onTrackEdit?(commentId: number);
    onMoveUp?(commentId: number);
    onSendHeart();
    onDidHighlighted?();
}

type Props = OwnProps;

export default class Comment extends React.PureComponent<Props, OwnState> {

    private container: HTMLDivElement;

    constructor(props: Props) {
        super(props);
        this.state = {
            hasHover: false,
            collapsed: true,
        };
    }

    public componentDidMount() {
        const {
            comment, highlighted, inForum, onDidHighlighted, onTrackEdit,
        } = this.props;

        if (highlighted) {
            this.container.scrollIntoView(true);
            if (onDidHighlighted) {
                onDidHighlighted();
            }
        }
        if (inForum && this.isEditable()) {
            onTrackEdit(comment.id);
        }
    }

    public componentDidUpdate(prevProps: Props) {
        const { highlighted, onDidHighlighted } = this.props;
        if (highlighted && !prevProps.highlighted) {
            this.container.scrollIntoView(true);
            if (onDidHighlighted) {
                onDidHighlighted();
            }
        }
    }

    public render() {
        const {
            comment, followLinks, inForum, inGroups, highlighted, hasBodyHiddenText, hasBodyDeletedStatus,
            hasFooterLike, hasFooterReply, hasMenuReport, hasMenuEdit, hasMenuDelete,
            requestUser, requestUserPermissions, moderators, isMobile, location,
            onReply, onDelete, onReport, onToggleMenuMore, onMoveUp, hasMenuMoveUp, hasMenu,
            onSendHeart, counselling, className,
        } = this.props;

        const inBlogs = !(inForum || inGroups);
        const isAdmin = inGroups
            ? requestUserPermissions.indexOf('users.can_manage_groups') >= 0
            : requestUserPermissions.indexOf('forum.can_manage_forum') >= 0;
        const isModerator = (inForum || inGroups) && isAuthenticatedAs(requestUser, moderators);
        const isOwner = isAuthenticatedAs(requestUser, comment.authorUser);

        let role = null;
        if (isOwner) {
            role = 'author';
        } else if (isModerator) {
            role = 'moderator';
        } else if (isAdmin) {
            role = 'admin';
        }

        const showComplaints = (inForum || inGroups) && !comment.deletedAt && (isAdmin || isModerator);
        const isCollapsedIfDeleted = this.state.collapsed;

        return (
            // TODO: Use Panel when blogs and forum will be merged
            <div
                id={`comment-${comment.id}`}
                className={cx(
                    styles.Comment, className,
                    inBlogs && styles['Comment--inBlogs'],
                    inForum && styles['Comment--inForum'],
                    inGroups && styles['Comment--inGroups'],
                    highlighted && styles['Comment--highlighted'],
                    comment.deletedAt && isCollapsedIfDeleted && styles['Comment--deletedCollapsed'],
                    comment.deletedAt && !isCollapsedIfDeleted && styles['Comment--deletedExpanded'],
                    !comment.deletedAt && !hasFooterLike && !hasFooterReply && styles['Comment--noFooter'],
                )}
                ref={this.handleConteinterRef}
                onMouseEnter={this.handleOnMouseEnter}
                onMouseLeave={this.handleOnMouseLeave}
            >
                {(inForum || inGroups) ? (
                    <div className={styles.Comments__forumLayout}>
                        <CommentHeader
                            className={cx(
                                styles.Comment__header,
                                comment.deletedAt && isCollapsedIfDeleted && styles['Comment__header--deletedCollapsed'],
                            )}
                            comment={comment}
                            isCollapsed={isCollapsedIfDeleted}
                            inForum={inForum}
                            inGroups={inGroups}
                            moderators={moderators}
                            onToggleDeletedDetails={this.handleOnToggleDeletedDetails}
                            counselling={counselling}
                        />

                        {hasMenu &&
                            <CommentMenu
                                className={styles.Comment__dropdownMenu}
                                requestUser={requestUser}
                                comment={comment}
                                inGroups={inGroups}
                                role={role}
                                hasReport={hasMenuReport}
                                hasEdit={hasMenuEdit && (isAdmin || (isOwner && this.isEditable()))}
                                hasDelete={hasMenuDelete}
                                onToggleMenuMore={onToggleMenuMore}
                                onReport={onReport}
                                onDelete={onDelete}
                                onMoveUp={onMoveUp}
                                hasMoveUp={hasMenuMoveUp && requestUserPermissions.indexOf('forum.can_manage_forum') && (comment.positionInPost > 1)}
                                onSendHeart={onSendHeart}
                            />
                        }

                        <CommentBody
                            comment={comment}
                            isCollapsed={isCollapsedIfDeleted}
                            inForum={inForum}
                            inGroups={inGroups}
                            followLinks={followLinks}
                            hasHiddenText={hasBodyHiddenText}
                            hasDeletedStatus={hasBodyDeletedStatus}
                        />

                        {counselling && counselling.counsellorSignature && (
                            <div className={styles.Comment__signature}>
                                <br/>
                                {linebreaksbr(boldize(linkize(counselling.counsellorSignature)))}
                            </div>
                        )}

                        <CommentFooter
                            className={styles.Comment__footer}
                            comment={comment}
                            inForum={inForum}
                            inGroups={inGroups}
                            requestUser={requestUser}
                            hasDelete={false}
                            hasId={false}
                            hasLike={hasFooterLike}
                            hasReply={hasFooterReply}
                            hasReport={false}
                            onDelete={onDelete}
                            onReply={onReply}
                            onReport={onReport}
                        />

                        {showComplaints && (
                            <Complaints
                                id={comment.id}
                                entity={inGroups ? 'photoblog-comment' : 'forum-message'}
                                deleteLabel={COMMENT_REMOVE_COMPLAINTS}
                            />
                        )}
                    </div>
                ) : (
                    <div className={styles.Comment__blogsLayout}>
                        <div className={styles.Comment__blogsLayoutCol1}>
                            <CommentHeader
                                className={cx(
                                    styles.Comment__header,
                                    comment.deletedAt && isCollapsedIfDeleted && styles['Comment__header--deletedCollapsed'],
                                )}
                                comment={comment}
                                isCollapsed={false}
                                inForum={false}
                                inGroups={false}
                                moderators={moderators}
                                onToggleDeletedDetails={this.handleOnToggleDeletedDetails}
                            />
                        </div>
                        <div className={styles.Comment__blogsLayoutCol2}>
                            <CommentBody
                                comment={comment}
                                isCollapsed={isCollapsedIfDeleted}
                                inForum={false}
                                inGroups={false}
                                followLinks={followLinks}
                                hasHiddenText={false}
                                hasDeletedStatus={hasBodyDeletedStatus}
                                location={location}
                            />

                            <CommentFooter
                                className={styles.Comment__footer}
                                comment={comment}
                                inForum={false}
                                inGroups={false}
                                requestUser={requestUser}
                                hasDelete={this.state.hasHover || isMobile}
                                hasId={isAdmin && this.state.hasHover}
                                hasLike={hasFooterLike}
                                hasReply={this.state.hasHover || isMobile}
                                hasReport={this.state.hasHover || isMobile}
                                onDelete={onDelete}
                                onReply={onReply}
                                onReport={onReport}
                            />
                        </div>
                    </div>
                )}
            </div>
        );
    }

    private isEditable = () => {
        const commentTime = new Date(this.props.comment.created);
        const commentTimeWithDelay = new Date((new Date (commentTime)).setMinutes(commentTime.getMinutes() + 2));
        const currentTime = new Date(Date.now());
        return commentTimeWithDelay > currentTime;
    };

    private handleConteinterRef = (r: HTMLDivElement) => {
        this.container = r;
    };

    private handleOnMouseEnter = () => {
        this.setState({ hasHover: true });
    };

    private handleOnMouseLeave = () => {
        this.setState({ hasHover: false });
    };

    private handleOnToggleDeletedDetails = () => {
        this.setState({ collapsed: !this.state.collapsed });
    };
}

interface PlaceholderOwnProps {
    style?: any;
}

export const CommentPlaceholder: React.StatelessComponent<PlaceholderOwnProps> = ({ style }) => (
    <div className={styles.CommentPlaceholder} style={style}>
        <div className={styles.CommentPlaceholder__avatar}>
            <LoadingPlaceholder width={32} height={32}/>
        </div>

        <div className={styles.CommentPlaceholder__body}>
            <LoadingPlaceholder width="100%" height={17}/>
            <LoadingPlaceholder width={150}/>
        </div>
    </div>
);
CommentPlaceholder.displayName = 'CommentPlaceholder';

interface CommentForumPlaceholderOwnProps {
    style?: any;
    className?: string;
}

export const CommentForumPlaceholder: React.StatelessComponent<CommentForumPlaceholderOwnProps> =
    ({ className, style }) => (
    <Panel className={cx(styles.CommentForumPlaceholder, className)} style={style}>
        <div className={styles.CommentForumPlaceholder__meta}>
            <LoadingPlaceholder width="50%" height={17}/>
        </div>
        <div className={styles.CommentForumPlaceholder__body}>
            <LoadingPlaceholder width="100%" height={17}/>
            <LoadingPlaceholder width="100%" height={17}/>
        </div>
        <LoadingPlaceholder width={150}/>
    </Panel>
);
CommentForumPlaceholder.displayName = 'CommentForumPlaceholder';
